import { config } from '../../../../../../config';
import { DeviceType } from '../../details/types';
import { ASSET_TYPE_TILES, allTypesAndBrands } from './assetTypeTileConfig';
import { BRAND_TILES, allBrandsWithType } from './brandTileConfig';
import type { TelematicsTile } from './types';

// Overview over (some) RIO connectors
// https://collaboration.msi.audi.com/confluence/pages/viewpage.action?spaceKey=TICON&title=Inbound+Connectors

export const TELEMATICS_TILES: Record<
    | 'daimlerFleetboard'
    | 'rioBox'
    | 'rioPocketDriver'
    | 'rioPocketDriverTest'
    | 'rio4DriversApp'
    | 'scaniaTelematics'
    | 'schmitzCargobull'
    | 'linkBox'
    | 'webFleet'
    | 'webFleetRetrofit'
    | 'manOemTelematicsVan'
    | 'manOemTelematicsTruckBus'
    | 'kroneTelematics'
    | 'volvoTelematics'
    | 'idemTelematics'
    | 'koegel'
    | 'schwarzmueller'
    | 'timocom'
    | 'noTelematics'
    | 'renault'
    | 'co3'
    | 'daf',
    TelematicsTile
> = {
    // Provided by MAN Core Connectivity
    // RIOTT-6600: from now on, only when selecting the RIO Box telematic, we will be selecting a TBM3 device indeed,
    // besides the fact that both TBM3 and CM4 devices will follow the same flow in the end.
    rioBox: {
        displayName: 'RIO Box',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.rio-box',
        deviceType: DeviceType.TBM3,
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        showForNonEu: true,
        suitableFor: [
            ...allBrandsWithType(ASSET_TYPE_TILES.truck),
            ...allBrandsWithType(ASSET_TYPE_TILES.bus),
            { type: ASSET_TYPE_TILES.van, brand: BRAND_TILES.man },
        ],
        oemFor: [],
    },

    daimlerFleetboard: {
        displayName: 'Fleetboard',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.fleetboard',
        deviceType: DeviceType.FLEETBOARD,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/fleetboard`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/fleetboard`,
        tileType: 'default',
        suitableFor: [
            { type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.daimler },
            { type: ASSET_TYPE_TILES.bus, brand: BRAND_TILES.daimler },
        ],
        oemFor: [
            { type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.daimler },
            { type: ASSET_TYPE_TILES.bus, brand: BRAND_TILES.daimler },
        ],
    },

    // Provided by MAN Core Connectivity only for MAN eTGE vans
    linkBox: {
        displayName: 'LINK Box',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.link-box',
        deviceType: DeviceType.MAN_WEBFLEET,
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.van, brand: BRAND_TILES.man }],
        oemFor: [],
    },

    // Provided by transport x. It syncs all data from Webfleet to RIO
    webFleet: {
        displayName: 'Webfleet',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.web-fleet',
        deviceType: DeviceType.WEBFLEET,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/webfleet`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/webfleet`,
        tileType: 'default',
        suitableFor: allTypesAndBrands(),
        oemFor: [],
    },

    webFleetRetrofit: {
        displayName: 'Webfleet RIO Edition',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.web-fleet-retrofit',
        deviceType: DeviceType.WEBFLEET_RETROFIT,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/webfleet-retrofit`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/webfleet-retrofit`,
        tileType: 'default',
        suitableFor: allTypesAndBrands(),
        featureToggleName: 'webfleetRetrofit',
        oemFor: [],
    },

    // Provided by MAN Core Connectivity only for MAN TGE vans
    manOemTelematicsVan: {
        displayName: 'MAN',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.man-telematic',
        deviceType: DeviceType.OCU3,
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.van, brand: BRAND_TILES.man }],
        oemFor: [{ type: ASSET_TYPE_TILES.van, brand: BRAND_TILES.man }],
    },

    // RIOTT-6600: from now on, only when selecting the MAN OEM telematic, we will be selecting a CM4 device indeed,
    // besides the fact that both TBM3 and CM4 devices will follow the same flow in the end.
    manOemTelematicsTruckBus: {
        displayName: 'MAN',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.man-oem-telematic-truck-bus',
        deviceType: DeviceType.CM4,
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        suitableFor: [
            { type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.man },
            { type: ASSET_TYPE_TILES.bus, brand: BRAND_TILES.man },
        ],
        oemFor: [
            { type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.man },
            { type: ASSET_TYPE_TILES.bus, brand: BRAND_TILES.man },
        ],
    },

    // The new mobile app to provide position data
    rioPocketDriver: {
        displayName: 'Pocket Driver',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.rio-pocket-driver',
        deviceType: DeviceType.POCKET_DRIVER_APP,
        //https://connector.assets.rio.cloud
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        suitableFor: allBrandsWithType(ASSET_TYPE_TILES.truck, ASSET_TYPE_TILES.van, ASSET_TYPE_TILES.bus),
        oemFor: [],
    },
    // The new mobile app to provide position data
    rioPocketDriverTest: {
        displayName: 'Pocket Driver Test',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.rio-pocket-driver',
        deviceType: DeviceType.POCKET_DRIVER_APP,
        //https://connector.assets.rio.cloud
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        suitableFor: allBrandsWithType(ASSET_TYPE_TILES.truck, ASSET_TYPE_TILES.van, ASSET_TYPE_TILES.bus),
        oemFor: [],
        featureToggleName: 'rioPocketDriverTest',
    },
    // The old legacy mobile app to provide position data
    rio4DriversApp: {
        displayName: 'RIO4Drivers App',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.rio-4-drivers',
        deviceType: DeviceType.TRUCK_LOGIN_DEVICE,
        // https://connector.assets.rio.cloud
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'default',
        suitableFor: allBrandsWithType(ASSET_TYPE_TILES.truck, ASSET_TYPE_TILES.van, ASSET_TYPE_TILES.bus),
        oemFor: [],
        featureToggleName: 'rio4DriversApp',
    },
    // Provided by transport X, Syncs Scania vehicles to RIO
    scaniaTelematics: {
        displayName: 'Scania Telematics',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.scania-telematic',
        deviceType: DeviceType.SCANIA_TELEMATICS,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/scania`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/scania`,
        tileType: 'default',
        suitableFor: [
            { type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.scania },
            { type: ASSET_TYPE_TILES.bus, brand: BRAND_TILES.scania },
        ],
        oemFor: [
            { type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.scania },
            { type: ASSET_TYPE_TILES.bus, brand: BRAND_TILES.scania },
        ],
    },
    kroneTelematics: {
        displayName: 'Krone',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.krone',
        deviceType: DeviceType.KRONE,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/krone`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/krone`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.krone }],
        oemFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.krone }],
        featureToggleName: 'krone',
    },
    schmitzCargobull: {
        displayName: 'Schmitz Cargobull',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.schmitz-cargobull',
        deviceType: DeviceType.SCHMITZ_CARGOBULL,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/cargobull`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/cargobull`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.schmitzCargobull }],
        oemFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.schmitzCargobull }],
    },
    volvoTelematics: {
        displayName: 'Volvo',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.volvo',
        deviceType: DeviceType.VOLVO_TELEMATICS,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/volvo`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/volvo`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.volvo }],
        oemFor: [{ type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.volvo }],
    },
    idemTelematics: {
        displayName: 'Idem Telematics',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.idem',
        deviceType: DeviceType.IDEM_TELEMATICS,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/idem`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/idem`,
        tileType: 'default',
        suitableFor: allBrandsWithType(ASSET_TYPE_TILES.trailer),
        oemFor: [],
        featureToggleName: 'idem',
    },
    koegel: {
        displayName: 'Kögel Telematics',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.koegel',
        deviceType: DeviceType.KOEGEL_TELEMATICS,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/koegel`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/koegel`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.koegel }],
        oemFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.koegel }],
        featureToggleName: 'koegel',
    },
    schwarzmueller: {
        displayName: 'SWiT',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.schwarzmueller',
        deviceType: DeviceType.SCHWARZMUELLER_TELEMATICS,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/schwarzmueller`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/schwarzmueller`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.schwarzmueller }],
        oemFor: [{ type: ASSET_TYPE_TILES.trailer, brand: BRAND_TILES.schwarzmueller }],
        featureToggleName: 'schwarzmueller',
    },
    timocom: {
        displayName: 'Timocom',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.timocom',
        deviceType: DeviceType.TIMOCOM,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/timocom`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/timocom`,
        tileType: 'default',
        suitableFor: allBrandsWithType(ASSET_TYPE_TILES.truck),
        oemFor: [],
        featureToggleName: 'timocom',
    },
    renault: {
        displayName: 'Renault',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.renault',
        deviceType: DeviceType.RENAULT,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/renault`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/renault`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.renaultTrucks }],
        oemFor: [{ type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.renaultTrucks }],
    },
    co3: {
        displayName: 'CO3',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.co3',
        deviceType: DeviceType.CO3,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/co3`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/co3`,
        tileType: 'default',
        suitableFor: allBrandsWithType(ASSET_TYPE_TILES.truck),
        oemFor: [],
        featureToggleName: 'co3',
    },
    daf: {
        displayName: 'DAF',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.daf',
        deviceType: DeviceType.DAF,
        connectorUri: `${config.external.iframe.easyOnboarding}/#/connect/daf`,
        prerequisitesUri: `${config.external.iframe.easyOnboarding}/#/prerequisites/daf`,
        tileType: 'default',
        suitableFor: [{ type: ASSET_TYPE_TILES.truck, brand: BRAND_TILES.daf }],
        oemFor: [],
    },
    noTelematics: {
        displayName: 'assets.assets.create-new-dialog.telematics.tiles.display-name.no-telematic',
        description: 'assets.assets.create-new-dialog.telematics.tiles.description.no-telematic',
        deviceType: DeviceType.NONE,
        connectorUri: `${config.external.iframe.assetConnector}/#/connect`,
        tileType: 'decent',
        showForNonEu: true,
        suitableFor: allTypesAndBrands(),
        oemFor: [],
    },
};

export const ALL_TELEMATICS_TILES = Object.values(TELEMATICS_TILES);

export const TELEMATICS_TILES_DISPLAYED_FIRST = [TELEMATICS_TILES.rioBox, TELEMATICS_TILES.rioPocketDriver];
